/**
 * Aba "Sondagem" da barra lateral. Curta, como as outras: liga a camada,
 * legenda, contagem por estado e situação dos dados. Aparência na engrenagem.
 */
import { CircleQuestionMark, ExternalLink, RefreshCw, Settings2, TowerControl } from 'lucide-react';
import { useMemo, useState } from 'react';
import { formatInt } from '../lib/format';
import { formatRelative } from '../lib/time';
import { useTimeStore } from '../state/time';
import { Badge, Button, Dialog, Hint, IconButton, Section, Slider, Toggle } from '../ui/primitives';
import { MARCO_SVG } from './marco';
import { FAIXAS_CAPE, IDADE_AUSENTE_H, estadoDe, perfilNoInstante } from './perfil';
import { LIMITES_SONDAGEM, useSoundingStore } from './store';
import { carregarPerfis } from './sync';

/** Marco de lançamento, na mesma forma do mapa. */
function Marco(props: { cor?: string; contorno: string; tracejado?: boolean }) {
  return (
    <svg viewBox="-1.3 -2.4 2.6 2.9" width={12} height={14} aria-hidden className="shrink-0">
      <path
        d={MARCO_SVG}
        fill="none"
        stroke={props.cor ?? props.contorno}
        strokeWidth={props.cor ? 0.38 : 0.28}
        strokeDasharray={props.tracejado ? '0.45 0.45' : undefined}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

/** O balão em si — o único círculo do mapa que representa a sonda. */
function BalaoLegenda(props: { cor: string }) {
  return (
    <svg viewBox="-2 -2 4 4" width={12} height={12} aria-hidden className="shrink-0">
      <circle cx="0" cy="0" r="1.6" stroke="rgba(255,255,255,0.9)" strokeWidth="0.45" fill={props.cor} />
    </svg>
  );
}

function AparenciaDialog(props: { open: boolean; onClose: () => void }) {
  const tamanho = useSoundingStore((s) => s.tamanho);
  const setTamanho = useSoundingStore((s) => s.setTamanho);
  return (
    <Dialog title="Aparência da radiossonda" open={props.open} onClose={props.onClose}>
      <div className="space-y-4 p-4">
        <Slider label="Tamanho" value={tamanho} {...LIMITES_SONDAGEM.tamanho} onChange={setTamanho} />
      </div>
    </Dialog>
  );
}

export function SoundingPanel() {
  const visivel = useSoundingStore((s) => s.visivel);
  const situacao = useSoundingStore((s) => s.situacao);
  const dados = useSoundingStore((s) => s.dados);
  const setVisivel = useSoundingStore((s) => s.setVisivel);
  const [aparencia, setAparencia] = useState(false);

  const displayedTime = useTimeStore((s) => s.displayedTime);
  const currentTime = useTimeStore((s) => s.currentTime);
  const instante = displayedTime ?? currentTime;

  const contagem = useMemo(() => {
    const c = { recente: 0, antiga: 0, ausente: 0, erro: 0 };
    for (const e of dados.estacoes) c[estadoDe(perfilNoInstante(dados.perfisDe.get(e.wmo), instante), instante)]++;
    return c;
  }, [dados, instante]);

  return (
    <div>
      <Section
        title="Radiossonda"
        icon={TowerControl}
        right={
          visivel && (
            <IconButton
              icon={Settings2}
              size="sm"
              label="Aparência da radiossonda"
              onClick={() => setAparencia(true)}
              className="pointer-coarse:size-11"
            />
          )
        }
      >
        <Toggle
          label="Perfis da atmosfera"
          checked={visivel}
          onChange={setVisivel}
          description="Balões lançados duas vezes por dia; o perfil abre no clique."
        />

        {visivel && (
          <>
            {dados.estacoes.length > 0 && (
              <div className="flex flex-wrap items-center gap-1.5 pt-2.5">
                <Badge tone="rio">{formatInt(contagem.recente)} no período</Badge>
                {contagem.antiga > 0 && <Badge>{formatInt(contagem.antiga)} anteriores</Badge>}
                {contagem.ausente > 0 && <Badge>{formatInt(contagem.ausente)} sem sondagem</Badge>}
                {contagem.erro > 0 && <Badge tone="alerta">{formatInt(contagem.erro)} com falha</Badge>}
              </div>
            )}

            <div className="space-y-2 pt-3">
              <div>
                <div className="flex gap-0.5">
                  {FAIXAS_CAPE.map((f, i) => (
                    <div key={i} className="flex-1" title={`${f.rotulo} — a partir de ${f.min} J/kg`}>
                      <div className="h-2.5 rounded-[2px]" style={{ background: f.cor }} />
                      <div className="mt-0.5 text-center text-[9px] text-ink-400 tabular-nums">{f.min}</div>
                    </div>
                  ))}
                </div>
                <Hint>Energia de convecção (J/kg) — o combustível de tempestade na coluna.</Hint>
              </div>
              <div className="flex flex-wrap gap-x-3 gap-y-1 text-[11px] text-ink-300">
                <span className="inline-flex items-center gap-1">
                  <BalaoLegenda cor={FAIXAS_CAPE[2]!.cor} /> o balão, na ponta da trilha
                </span>
                <span className="inline-flex items-center gap-1">
                  <Marco cor={FAIXAS_CAPE[2]!.cor} contorno="rgba(255,255,255,0.8)" /> estação sem percurso publicado
                </span>
                <span className="inline-flex items-center gap-1">
                  <Marco contorno="#7d8896" tracejado /> sem sondagem
                </span>
              </div>
            </div>
          </>
        )}
      </Section>

      <Section title="Dados" icon={RefreshCw} defaultOpen={false}>
        <div className="space-y-1.5 text-xs text-ink-300">
          {situacao.estado === 'loading' && <div>Baixando…</div>}
          {situacao.estado === 'error' && <div className="text-alerta-300">Falha ao baixar: {situacao.erro}</div>}
          {situacao.estado === 'ready' && (
            <div>
              {formatInt(dados.estacoes.length)} estações
              {dados.gerado !== null && <> · atualizado {formatRelative(dados.gerado)}</>}
            </div>
          )}
          <Button icon={RefreshCw} onClick={() => void carregarPerfis(true)}>
            Atualizar agora
          </Button>
        </div>
      </Section>

      <Section title="O que é" icon={CircleQuestionMark} defaultOpen={false}>
        <div className="space-y-2 text-xs leading-relaxed text-ink-300">
          <p>
            Um balão sobe da estação medindo temperatura, umidade e pressão até uns 16 km. É a única medida direta do
            que está acontecendo <strong className="text-white">acima</strong> do solo — o satélite vê de cima, a chuva
            se mede embaixo, e a sondagem atravessa a coluna inteira.
          </p>
          <p>
            São duas por dia, às 00Z e 12Z, então a camada quase não se move no loop. Estações falham com frequência:
            sem sondagem em {IDADE_AUSENTE_H} h, o balão aparece tracejado.
          </p>
          <p className="text-ink-400">
            Fonte: University of Wyoming, Department of Atmospheric Science.{' '}
            <a
              href="https://weather.uwyo.edu/upperair/"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-0.5 text-rio-300 hover:underline"
            >
              weather.uwyo.edu
              <ExternalLink size={10} />
            </a>
          </p>
        </div>
      </Section>

      <AparenciaDialog open={aparencia} onClose={() => setAparencia(false)} />
    </div>
  );
}
